import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDefaultToast } from '@/composables/useDefaultToast';
import { getJobExperienceOptions } from '@/helpers/filterOptions';
import { resolveJobDisplayName } from '@/helpers/workerJob';
import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import workerService from '@/modules/worker/services/workerService';
import {
	WORKER_JOB_EDITOR_INITIAL,
	type WorkerJobEditorJob,
	type WorkerJobFormatted,
	type WorkerJobsEditorProps,
} from '@/modules/worker/types/workerJobsEditor';
import { useConfigurationStore } from '@/store/configurationStore';
import { JobExperience } from '@/types/job';

import type { BadgeProps } from '@/dsComponents/Badge';
import type { ButtonProps } from '@/dsComponents/buttons/types';
import type { TableAction, TableColumnProps } from '@/dsComponents/Table/types';
import type { WorkerDetail, WorkerJob } from '@/modules/worker/types';
import type { LabelValue } from '@/types/common';

import Badge from '@/dsComponents/Badge/Badge.vue';

export const useWorkerJobsEditor = (props: WorkerJobsEditorProps) => {
	const isAsideLayout = computed(() => props.asideLayout ?? false);

	const configStore = useConfigurationStore();

	const { t } = useI18n();
	const { toastError } = useDefaultToast();
	const patchWorkerLocally =
		inject<(value: Partial<WorkerDetail>) => void>('workerPatchLocally');

	const isEditJobModalOpen = ref(false);
	const isNewJob = ref(true);
	const isAlertDeleteModalOpen = ref(false);

	const isUpdateWorkerLoading = ref(false);

	const jobNameSelected = ref<string>('');
	const degreeSelected = ref<string>('');
	const experienceSelected = ref<string>('');

	const jobToUpdate = ref<WorkerJobEditorJob>({ ...WORKER_JOB_EDITOR_INITIAL });

	const degreeOptions = computed<{ label: string; value: string }[]>(() =>
		configStore.degreeLevels.map(
			(degreeLevel: { name: string; slug?: string; value?: string }) => ({
				label: degreeLevel.name,
				value: degreeLevel.slug ?? degreeLevel.value ?? '',
			})
		)
	);

	const experienceOptions = computed<LabelValue[]>(() =>
		getJobExperienceOptions()
	);

	const formatJob = (
		job: WorkerJobEditorJob,
		jobName?: string | null
	): WorkerJobFormatted | undefined => {
		const displayName = resolveJobDisplayName({ jobId: job.jobId, jobName });
		const degreeLevelLabel = degreeOptions.value.find(
			(option) => option.value === job.degreeLevel
		)?.label;
		let experienceLabel: BadgeProps | undefined;
		let experienceText: string | undefined;
		if (job.experience) {
			const shortLabel = t(`jobExperienceShort.${job.experience}`);
			experienceText = shortLabel;
			experienceLabel = {
				label: shortLabel,
				color: 'neutral',
			} as BadgeProps;
		}
		return {
			jobId: job.jobId,
			experienceLabel,
			experienceText,
			degreeLevelLabel,
			name: displayName,
			degreeLevel: job.degreeLevel ?? undefined,
			experience: normalizeJobExperience(job.experience),
		};
	};

	const workerJobsFormatted = ref<WorkerJobFormatted[]>([]);

	watch(
		() => props.worker?.jobs,
		() => {
			const jobs = props.worker?.jobs ?? [];
			workerJobsFormatted.value = jobs
				.map((job) =>
					formatJob(
						{
							jobId: job.jobId,
							degreeLevel: job.degreeLevel ?? undefined,
							experience: normalizeJobExperience(job.experience),
						},
						job.jobName
					)
				)
				.filter(
					(job: WorkerJobFormatted | undefined): job is WorkerJobFormatted =>
						job !== undefined
				);
		},
		{ immediate: true }
	);

	const jobOptions = computed<LabelValue<string>[]>(() => {
		if (workerJobsFormatted.value.length > 0) {
			const workerJobLabel: string[] = workerJobsFormatted.value.map(
				(job) => job.name
			);
			return configStore.jobOptions.filter(
				// biome-ignore lint/style/noNonNullAssertion: Init Biome
				(job: LabelValue<string>) => !workerJobLabel.includes(job.label!)
			);
		}
		return configStore.jobOptions;
	});

	const onClickEditJob = (jobToEditId: string) => {
		// biome-ignore lint/style/noNonNullAssertion: Init Biome
		const { name, degreeLevel, experience } = workerJobsFormatted.value.find(
			(job) => job.jobId === jobToEditId
		)!;
		jobToUpdate.value = {
			jobId: jobToEditId,
			degreeLevel,
			experience,
		};

		jobNameSelected.value = name;
		degreeSelected.value =
			degreeOptions.value.find((degree) => degree.value === degreeLevel)
				?.label || '';
		experienceSelected.value =
			experienceOptions.value.find((option) => option.value === experience)
				?.label || '';

		isNewJob.value = false;
		isEditJobModalOpen.value = true;
	};

	const resetModalFields = () => {
		jobNameSelected.value = '';
		degreeSelected.value = '';
		experienceSelected.value = '';

		jobToUpdate.value = { ...WORKER_JOB_EDITOR_INITIAL };
		isNewJob.value = true;
	};

	const onClickAddNewJob = () => {
		resetModalFields();
		isEditJobModalOpen.value = true;
	};

	const onClickDeleteJob = (jobIdToDelete: string) => {
		jobToUpdate.value.jobId = jobIdToDelete;
		isAlertDeleteModalOpen.value = true;
	};

	const onClickCloseEditJobModal = () => {
		isEditJobModalOpen.value = false;
		resetModalFields();
	};

	const addWorkerJob = async (): Promise<boolean> => {
		try {
			isUpdateWorkerLoading.value = true;
			const job = jobToUpdate.value;
			const jobOption = configStore.getJobOption(job.jobId);
			const experience =
				normalizeJobExperience(job.experience) ?? JobExperience.NONE;
			await workerService.addJob(props.worker.id, {
				jobId: job.jobId,
				jobCode: jobOption?.slug ?? '',
				jobName: jobOption?.label ?? '',
				experience,
				degreeLevel: job.degreeLevel || null,
			});
			return true;
		} catch {
			toastError();
			return false;
		} finally {
			isUpdateWorkerLoading.value = false;
		}
	};

	const editWorkerJob = async (): Promise<boolean> => {
		try {
			isUpdateWorkerLoading.value = true;
			const { jobId, degreeLevel, experience } = jobToUpdate.value;
			const mappedExp =
				normalizeJobExperience(experience) ?? JobExperience.NONE;
			await workerService.patchJobExperience(props.worker.id, jobId, {
				experience: mappedExp,
				degreeLevel: degreeLevel || null,
			});
			return true;
		} catch {
			toastError();
			return false;
		} finally {
			isUpdateWorkerLoading.value = false;
		}
	};

	const buildWorkerJob = (editorJob: WorkerJobEditorJob): WorkerJob => {
		const label = configStore.getJobOption(editorJob.jobId)?.label ?? null;
		return {
			jobId: editorJob.jobId,
			jobName: label,
			experience: (normalizeJobExperience(editorJob.experience) ??
				null) as JobExperience | null,
			degreeLevel: editorJob.degreeLevel ?? null,
		};
	};

	const deleteWorkerJobs = async () => {
		try {
			isUpdateWorkerLoading.value = true;
			const { jobId } = jobToUpdate.value;
			await workerService.deleteJob(props.worker.id, jobId);
			const nextJobs = (props.worker.jobs ?? []).filter(
				(job) => job.jobId !== jobId
			);
			patchWorkerLocally?.({ jobs: nextJobs } as Partial<WorkerDetail>);
			isAlertDeleteModalOpen.value = false;
		} catch {
			toastError();
		} finally {
			isUpdateWorkerLoading.value = false;
		}
	};

	const handleSave = async () => {
		const success = isNewJob.value
			? await addWorkerJob()
			: await editWorkerJob();
		if (success) {
			isEditJobModalOpen.value = false;
			const nextJob = buildWorkerJob(jobToUpdate.value);
			const currentJobs = props.worker.jobs ?? [];
			const nextJobs = isNewJob.value
				? [...currentJobs, nextJob]
				: currentJobs.map((job) =>
						job.jobId === jobToUpdate.value.jobId ? nextJob : job
					);
			patchWorkerLocally?.({ jobs: nextJobs } as Partial<WorkerDetail>);
			resetModalFields();
		}
	};

	const handleJobSelect = (option: { label?: string; value?: unknown }) => {
		jobToUpdate.value.jobId = String(option.value ?? '');
		jobNameSelected.value = option.label ?? '';
	};

	const handleJobUpdate = (key: keyof WorkerJobEditorJob, value: string) => {
		if (key === 'experience') {
			jobToUpdate.value = {
				...jobToUpdate.value,
				experience: normalizeJobExperience(value),
			};
			return;
		}
		jobToUpdate.value = { ...jobToUpdate.value, [key]: value };
	};

	const tableActions = computed<TableAction[]>(() => {
		if (props.readonly) return [];

		const editAction: TableAction = {
			button: {
				iconOnly: 'edit',
				type: 'neutral',
				variant: 'ghost',
				size: 'sm',
			} as ButtonProps,
			onClick: (data: WorkerJobFormatted) => onClickEditJob(data.jobId),
		};
		const trashAction: TableAction = {
			button: {
				iconOnly: 'trash',
				type: 'error',
				variant: 'ghost',
				size: 'sm',
			},
			onClick: (data: WorkerJobFormatted) => {
				onClickDeleteJob(data.jobId);
			},
		};

		if (isAsideLayout.value) {
			return [trashAction];
		}
		return [editAction, trashAction];
	});

	const columns = computed<TableColumnProps[]>(() => {
		const cols: TableColumnProps[] = [{ field: 'name', header: t('form.job') }];
		if (!isAsideLayout.value) {
			cols.push({
				field: 'degreeLevelLabel',
				header: t('page.interim.administrativeDocuments.degree'),
				width: '110px',
			});
		}
		cols.push({
			field: 'experienceLabel',
			header: t('form.experience'),
			component: Badge,
			width: '120px',
		});
		return cols;
	});

	return {
		t,
		isEditJobModalOpen,
		isNewJob,
		isAlertDeleteModalOpen,
		isUpdateWorkerLoading,
		jobNameSelected,
		degreeSelected,
		experienceSelected,
		jobToUpdate,
		workerJobsFormatted,
		jobOptions,
		degreeOptions,
		experienceOptions,
		tableActions,
		columns,
		onClickEditJob,
		onClickAddNewJob,
		onClickDeleteJob,
		onClickCloseEditJobModal,
		deleteWorkerJobs,
		handleSave,
		handleJobSelect,
		handleJobUpdate,
	};
};
